// @givmo/app-admin — tolerant decode of the employer security surface (AS-WEB7 / AS-BE19 / AS-BE20,
// and AS-BE49's count for AS-BE38 (web)).
//
// Hand-decoded off `unknown` for the reason `sso-decode.ts` states: every route here postdates the
// committed OpenAPI snapshot, so there is no generated type for any of these shapes.
//
// ══ THE RULE THAT DECIDES EVERY BRANCH ════════════════════════════════════════════════════════
// ⚠ A VALUE THIS BUILD CANNOT READ IS "NOT REPORTED" — never a number, never a verdict. The session
// reset's counts and the export's effective window are both read immediately before the screen makes
// a claim to an Owner, and a manufactured zero or a manufactured "unclamped" would be that claim made
// on evidence that never arrived. `agent_docs/api-client.md` states the general form; the two shapes
// below are that rule with different consequences.

/** A non-negative integer count, or `null` for "not reported". Never coerces towards zero. */
function readCount(source: unknown, key: string): number | null {
  const record =
    typeof source === 'object' && source !== null ? (source as Record<string, unknown>) : {};
  const value = record[key];
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  const floored = Math.floor(value);
  return floored >= 0 ? floored : null;
}

// ── `SessionResetOut` (AS-BE19) ─────────────────────────────────────────────────────────────────

/**
 * What an Owner is told after a workforce-wide session reset. COUNTS, NEVER A ROSTER — the backend
 * deliberately returns no names, and this screen has nothing to add to that.
 *
 * ★ `failed` IS THE FIELD THAT MATTERS AND IT IS TRI-STATE. The reset is best-effort per employee, so
 * a partial run is a real outcome; reporting only `revoked` would let it read as a complete one,
 * which on an incident-response control is the dishonest direction. `null` means the count did not
 * arrive, and the screen says so rather than showing a reassuring `0`.
 */
export interface SessionResetOutcome {
  /** Roster members visited. Company admins who are not linked employees are never on it. */
  employees: number | null;
  revoked: number | null;
  failed: number | null;
}

export function readSessionReset(source: unknown): SessionResetOutcome {
  return {
    employees: readCount(source, 'employees'),
    revoked: readCount(source, 'revoked'),
    failed: readCount(source, 'failed'),
  };
}

/**
 * Whether the run left work behind — the only state with a remedy attached.
 *
 * ⚠ Only an explicit, readable count above zero. `null` does NOT report a problem (asserting a
 * failure from an absent count is as wrong as asserting success), and `0` is the clean run.
 * {@link resetCountsComplete} is the separate question of whether the screen may report at all.
 */
export function resetHadFailures(outcome: SessionResetOutcome): boolean {
  return outcome.failed !== null && outcome.failed > 0;
}

/**
 * Whether the response carried the NUMBERS, not merely a 200.
 *
 * Same shape as the L4 ceremony's `countsComplete`, and for the same reason: this is a tolerant
 * reader, so a `{}` body decodes to a perfectly defined all-`null` object. A screen gating on "the
 * request returned" would tell an Owner their workforce was signed out having been shown nothing.
 */
export function resetCountsComplete(outcome: SessionResetOutcome): boolean {
  return outcome.employees !== null && outcome.revoked !== null && outcome.failed !== null;
}

// ── One employee's company sign-in reset (AS-BE96, D-AS-70) ───────────────────────────────────

/**
 * What the reset answered: whether THIS request cleared a binding.
 *
 * `true` only for an explicit `released: true`; `false` only for an explicit `false` (nothing was
 * connected, so nothing changed); `null` for anything else — a 200 this build cannot read, which the
 * screen must not report as either.
 */
export function readSsoIdentityRelease(source: unknown): boolean | null {
  const value = ((source ?? {}) as Record<string, unknown>).released;
  return value === true ? true : value === false ? false : null;
}

/**
 * Whether the reset's email to the employee went out (`email_sent`, review-80 F4), so the screen says
 * "We've emailed them" only when the server says it did. Tri-state like `released`: `null` for an
 * answer this build cannot read, which claims no email either way.
 */
export function readSsoIdentityReleaseEmailSent(source: unknown): boolean | null {
  const value = ((source ?? {}) as Record<string, unknown>).email_sent;
  return value === true ? true : value === false ? false : null;
}

// ── The Owner's heads-up count (AS-BE49, read by AS-BE38 (web)) ────────────────────────────────

/**
 * `employees_without_second_factor` off a `GET /admin/company/settings/auth-policy` body: how many
 * linked employees the company two-factor requirement WOULD ask to add a second factor or unlink —
 * linked, not bound by the L4 lock, no server-verified factor — or `null` when the body carries no
 * readable count.
 *
 * ★ `null` IS THE FORWARD-COMPAT SIGNAL, NOT A MISSING VALUE TO PAPER OVER. The backend OMITS the key
 * (never sends `null`, never 403s) in three cases, and the Security tab must look exactly as it did
 * before AS-BE38 in all of them: the requirement (`EMPLOYER_MFA_LINK_REQUIREMENT_ENABLED`) is dark,
 * the caller is a Member rather than an Owner, or the backend predates AS-BE49. So the tab reveals
 * its new surface on a NUMBER arriving here and on nothing else, and never coerces an unreadable
 * value towards zero: "nobody would be asked" is a claim, and it needs the server's number behind it.
 *
 * The count is the requirement's population whatever the stored tier and the enforcement ceiling,
 * deliberately (it warns BEFORE Required is saved). So it can disagree with the roster's statuses,
 * which follow the effective tier — and the screen words it "would", never "are".
 */
export function readEmployeesWithoutSecondFactor(body: unknown): number | null {
  return readCount(body, 'employees_without_second_factor');
}

// ── The audit export's effective window (AS-BE20) ───────────────────────────────────────────────

/** The three headers the export reports its effective window and its bound on. */
export const AUDIT_WINDOW_START_HEADER = 'X-Givmo-Audit-Window-Start';
export const AUDIT_WINDOW_END_HEADER = 'X-Givmo-Audit-Window-End';
export const AUDIT_RETENTION_DAYS_HEADER = 'X-Givmo-Audit-Retention-Days';

/**
 * What the server said the export actually covered.
 *
 * ⚠ EVERY FIELD IS `null`-ABLE, AND `null` MEANS "COULD NOT READ IT" — which is a live possibility
 * rather than a defensive flourish. These are non-safelisted response headers on a CROSS-ORIGIN
 * request (the SPA is on Firebase Hosting, the API on its own origin), so the browser hides them
 * unless the backend names them in `Access-Control-Expose-Headers`. When it does not, `headers.get`
 * answers `null` for all three no matter what the server sent.
 *
 * That is exactly why {@link auditWindowWasClamped} is tri-state rather than boolean: the screen must
 * be able to say NOTHING about the window, and must never say "you got the range you asked for" on
 * evidence it did not receive.
 */
export interface AuditWindow {
  /** The effective first instant, as the server reported it. `null` = unreadable. */
  start: Date | null;
  /** The effective last instant. `null` = unreadable. */
  end: Date | null;
  /** The retention bound in days. `null` = unreadable — the screen then states no bound at all. */
  retentionDays: number | null;
}

function readHeaderDate(headers: Headers, name: string): Date | null {
  const raw = headers.get(name);
  if (raw === null || raw.trim().length === 0) return null;
  const at = new Date(raw);
  return Number.isNaN(at.getTime()) ? null : at;
}

export function readAuditWindow(response: Response): AuditWindow {
  const days = Number(response.headers.get(AUDIT_RETENTION_DAYS_HEADER));
  return {
    start: readHeaderDate(response.headers, AUDIT_WINDOW_START_HEADER),
    end: readHeaderDate(response.headers, AUDIT_WINDOW_END_HEADER),
    // `Number(null)` is 0 and `Number('')` is 0, so a bare finiteness check would manufacture a
    // retention bound of zero days out of a header that never arrived.
    retentionDays: Number.isInteger(days) && days > 0 ? days : null,
  };
}

/**
 * Whether the server served a LATER start than the Owner asked for — the reported clamp.
 *
 * ★ TRI-STATE, AND THE THIRD STATE IS THE POINT. A `from` reaching past the retention floor is
 * clamped and reported, and this program bans a silent clamp — so `true` must be surfaced. But the
 * report lives in a response header that a browser may refuse to hand over (see {@link AuditWindow}),
 * and in that case the honest answer is `null`, "not reported", NOT `false`. Returning `false` there
 * would have the screen assert the range was honoured on evidence it never received, which is the
 * silent clamp wearing a different hat.
 *
 * `null` also for a request that named no `from`: nothing was asked for, so nothing was overridden.
 *
 * The comparison is by UTC CALENDAR DAY on purpose. `from` is a `date` param while the header
 * carries a full instant (the start of that day, or the floor), so an exact match is a day match —
 * comparing the raw values would report a clamp on every request that was honoured.
 */
export function auditWindowWasClamped(
  requestedFrom: string | undefined,
  window: AuditWindow,
): boolean | null {
  if (requestedFrom === undefined || window.start === null) return null;
  const asked = Date.parse(`${requestedFrom}T00:00:00Z`);
  if (Number.isNaN(asked)) return null;
  return utcDayNumber(window.start.getTime()) > utcDayNumber(asked);
}

/** Whole UTC days since the epoch — the grain both sides of the clamp comparison share. */
function utcDayNumber(ms: number): number {
  return Math.floor(ms / 86_400_000);
}

/**
 * `2026-08-26` in UTC — for an INSTANT, where the UTC calendar day is the meaningful one.
 *
 * ⚠ NOT FOR A PICKED CALENDAR DAY. A day someone picked is already a calendar day (the export card's
 * native date fields hand it over as `YYYY-MM-DD`); serializing local midnight to UTC ships the
 * previous day for most of the world.
 */
export function toUtcDay(at: Date): string {
  return at.toISOString().slice(0, 10);
}

/** "August 26, 2026", or `null` for an instant this build could not read. */
export function formatWindowDay(at: Date | null): string | null {
  if (at === null) return null;
  return at.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * The default range the picker opens on: the whole retained window, ending today.
 *
 * Derived from the retention bound the SERVER states rather than from a constant here, so the two
 * cannot drift — but the bound only arrives on a response, so before the first download this falls
 * back to naming no start at all (the endpoint's own default) rather than guessing one.
 */
export function defaultExportRange(
  now: Date,
  retentionDays: number | null,
): { from?: string; to: string } {
  const to = toUtcDay(now);
  if (retentionDays === null) return { to };
  return { from: toUtcDay(new Date(now.getTime() - retentionDays * 86_400_000)), to };
}

/** Whether a chosen range is one the endpoint would accept — an inverted range is REFUSED there. */
export function rangeIsOrdered(from: string, to: string): boolean {
  if (from.length === 0 || to.length === 0) return true;
  return from <= to;
}
